import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, GraduationCap, HandHeart, Heart } from 'lucide-react';
import { HamburgerMenu } from '../components/HamburgerMenu';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { SEO } from '../components/SEO';
const SITE_URL = 'https://www.groundedlivermore.com';
const schools: {
  name: string;
  url: string;
}[] = [{
  name: 'Altamont Creek Elementary School',
  url: 'https://altamont.livermoreschools.org/'
}, {
  name: 'Arroyo Seco Elementary School',
  url: 'https://seco.livermoreschools.org/'
}, {
  name: 'Emma C. Smith Elementary School',
  url: 'https://smith.livermoreschools.org/'
}, {
  name: 'Jackson Avenue Elementary School',
  url: 'https://jackson.livermoreschools.org/'
}, {
  name: 'Joe Michell TK-8 School',
  url: 'https://michell.livermoreschools.org/'
}, {
  name: 'Junction Avenue TK-8 School',
  url: 'https://junction.livermoreschools.org/'
}, {
  name: 'Lawrence Elementary School',
  url: 'https://lawrence.livermoreschools.org/'
}, {
  name: 'Leo R. Croce Elementary School',
  url: 'https://croce.livermoreschools.org/'
}, {
  name: 'Marylin Avenue STEAM Academy',
  url: 'https://marylin.livermoreschools.org/'
}, {
  name: 'Rancho Las Positas Elementary School',
  url: 'https://rancho.livermoreschools.org/'
}, {
  name: 'Sunset Elementary School',
  url: 'https://sunset.livermoreschools.org/'
}];
const causes: string[] = ['Tri-Valley Haven (Livermore, CA) - shelter and support for survivors of domestic violence and sexual assault, and services for neighbors facing homelessness.', 'Goodness Village (Livermore, CA) - a local tiny-home community offering stability and dignity to neighbors experiencing chronic homelessness.', 'Livermore Middle Schools Music Boosters (Livermore, CA) - keeping music alive in our local middle schools.', 'Hope Hospice (Dublin, CA) - compassionate end-of-life care and grief support for Tri-Valley families.', "Ruby's Place (East Bay) - safety and support for survivors of domestic violence and human trafficking.", "Alzheimer's Association - care, support, and research for families facing Alzheimer's and dementia.", 'Camp Okizu - camp experiences and support for California families affected by childhood cancer.', 'National Pediatric Cancer Foundation - research toward safer, more effective treatments for kids with cancer.', 'MetaVivor - research funding dedicated to stage 4 metastatic breast cancer.', 'Fight Pretty - support and encouragement for those in the fight against breast cancer.', 'The Trevor Project - crisis support and suicide prevention for LGBTQ+ young people.', "...plus many other causes - and local families and neighbors - we've been honored to help."];
export function Community() {
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-287" data-mp="1sgvip0ksof73">
      <SEO title="Grounded in the Community - Giving Back in Livermore, CA | Grounded" description="Grounded gives back to Livermore - 10% back to every Livermore-district elementary school when you name yours, plus fundraising for the Alzheimer's Association, Hope Hospice, Goodness Village and more." canonical={`${SITE_URL}/community`} data-id="element-288" data-mp="1kz7edqw8bhu5" />
      <HamburgerMenu data-id="element-289" data-mp="23wqeu64mfs4y" />

      <main data-id="element-290" data-mp="23x9ybr9x6ol3">
        {/* Header */}
        <header className="pt-32 pb-16 px-6 md:px-12 lg:px-24 max-w-4xl mx-auto" data-id="element-291" data-mp="23w8k5dxlxlz5">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-292" data-mp="1yz8bcv9di54c">
            <div className="flex items-center gap-3 mb-6" data-id="element-293" data-mp="1oypn7oas02gm">
              <div className="h-px w-12 bg-[#D97E3A]" data-id="element-294" data-mp="ijnlk7z5rkpk" />
              <span className="text-[#D97E3A] font-inter font-medium tracking-widest text-xs uppercase" data-id="element-295" data-mp="il8ho214fbpj">
                Giving Back
              </span>
            </div>

            <h1 className="font-bebas text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-8" data-id="element-296" data-mp="22ei00lsur2nw">
              Grounded in the Community
            </h1>

            <p className="font-inter text-lg text-white/70 leading-relaxed border-l-2 border-[#D97E3A] pl-6" data-id="element-297" data-mp="277kz19gef4bh">
              "Coffee is community" isn't just a line on our wall - it's the
              reason we opened. From day one, Grounded was built to be a corner
              that gives back to the neighborhood it sits in. Here's some of
              what that looks like.
            </p>
          </motion.div>
        </header>

        {/* 10% back to schools */}
        <section aria-labelledby="schools-heading" className="px-6 md:px-12 lg:px-24 py-20 bg-[#111] border-y border-white/10" data-id="element-298" data-mp="2b6a84puelzpb">
          <div className="max-w-4xl mx-auto" data-id="element-299" data-mp="zs6czgc2y5io">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-300" data-mp="zqd7i4d7drc9">
              <GraduationCap className="text-[#D97E3A] mb-5" size={38} strokeWidth={1.5} aria-hidden="true" data-id="element-301" data-mp="3j0vcvi2i46qy" />
              <h2 id="schools-heading" className="font-bebas text-5xl md:text-6xl leading-none mb-8" data-id="element-302" data-mp="3ryns6m883eec">
                10% Back to Our <span className="text-[#D97E3A]" data-id="element-303" data-mp="307hlfmxzyig3">Schools</span>
              </h2>

              <div className="space-y-6 font-inter text-lg text-white/70 leading-relaxed" data-id="element-304" data-mp="2v9pwn1snfcg4">
                <p data-id="element-305" data-mp="390cm03cirpzj">
                  We work directly with local school PTAs and PTOs on a simple
                  idea: your coffee can support your kids' school.
                </p>
                <p data-id="element-306" data-mp="2gka9rww8nr5p">
                  Here's how it works - when you order, just tell us your
                  school, and we'll give 10% of your purchase back to that
                  school. Spend $20 on breakfast for the family, and $2 goes
                  straight to, say, Sunset Elementary. No apps, no punch cards,
                  no fine print - just say the name, and we take care of the
                  rest.
                </p>
                <p data-id="element-307" data-mp="p96qps36ehgf">
                  It's one of our favorite ways to turn a morning coffee run
                  into books, supplies, and programs for Livermore kids.
                </p>
                <p data-id="element-308" data-mp="3spnmbrq74vxj">
                  The program includes every elementary school in the Livermore
                  district:
                </p>
              </div>

              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 list-disc pl-5 marker:text-[#D97E3A]" data-id="element-309" data-mp="6hnovokw19fz">
                {schools.map(school => <li key={school.name} className="pl-1" data-id="element-310" data-mp="bhs47ba27y4m">
                    <a href={school.url} target="_blank" rel="noopener noreferrer" className="font-inter text-base text-white/75 underline underline-offset-4 decoration-white/20 hover:text-[#D97E3A] hover:decoration-[#D97E3A] transition-colors" data-id="element-311" data-mp="1hxcfcpxcoqlj">
                      {school.name}
                    </a>
                  </li>)}
              </ul>

              <p className="mt-8 font-inter text-lg text-white/70 leading-relaxed" data-id="element-312" data-mp="2sp1xypjm321o">
                Just name yours when you order - we'll take care of the rest.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Causes */}
        <section aria-labelledby="causes-heading" className="px-6 md:px-12 lg:px-24 py-20" data-id="element-313" data-mp="2jmsqy9njxqt1">
          <div className="max-w-4xl mx-auto" data-id="element-314" data-mp="2equ5attlymwb">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-315" data-mp="29qy1aukwuvgr">
              <Heart className="text-[#D97E3A] mb-5" size={38} strokeWidth={1.5} aria-hidden="true" data-id="element-316" data-mp="7z2sqntyb72r" />
              <h2 id="causes-heading" className="font-bebas text-5xl md:text-6xl leading-none mb-8" data-id="element-317" data-mp="3hwmsx1luag0v">
                Causes We've Been Proud to Support
              </h2>

              <p className="font-inter text-lg text-white/70 leading-relaxed mb-8" data-id="element-318" data-mp="386ab3h5et0yf">
                Community means showing up when it counts. Over the years, we've
                hosted fundraisers and given back to organizations - local and
                national - doing work we believe in, including:
              </p>

              <ul className="space-y-4 list-disc pl-5 marker:text-[#D97E3A]" data-id="element-319" data-mp="2kcacdew3svwt">
                {causes.map(cause => <li key={cause} className="font-inter text-base text-white/65 leading-relaxed pl-1" data-id="element-320" data-mp="iisvdkm32ipe">
                    {cause}
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Give back with us */}
        <section aria-labelledby="partner-heading" className="px-6 md:px-12 lg:px-24 py-20 bg-[#111] border-y border-white/10" data-id="element-321" data-mp="231wpkqdklkf1">
          <div className="max-w-3xl mx-auto text-center" data-id="element-322" data-mp="2cz1a6lrrp7tq">
            <HandHeart className="text-[#D97E3A] mx-auto mb-5" size={38} strokeWidth={1.5} aria-hidden="true" data-id="element-323" data-mp="2cxggszh6vuxg" />
            <h2 id="partner-heading" className="font-bebas text-4xl md:text-5xl leading-none mb-6" data-id="element-324" data-mp="2mnt1crp07o3l">
              Want to Give Back With Us?
            </h2>

            <p className="font-inter text-lg text-white/70 leading-relaxed mb-8" data-id="element-325" data-mp="2vqk9oiml23r0">
              Hosting a fundraiser, a school event, or a community gathering?
              Our{' '}
              <Link to="/events" className="text-[#D97E3A] underline underline-offset-4 decoration-[#D97E3A]/40 hover:decoration-[#D97E3A] transition-colors" data-id="element-326" data-mp="11c9meelmzhnc">
                community space
              </Link>{' '}
              and{' '}
              <Link to="/catering" className="text-[#D97E3A] underline underline-offset-4 decoration-[#D97E3A]/40 hover:decoration-[#D97E3A] transition-colors" data-id="element-327" data-mp="b9h7q2l1qtme">
                catering
              </Link>{' '}
              are made for exactly that - and we love partnering with local
              causes.
            </p>

            <p className="font-bebas text-2xl md:text-3xl tracking-wide text-white mb-8" data-id="element-328" data-mp="u93igni0rttd">
              Coffee is community. Thanks for being part of ours.
            </p>

            <Link to="/location" className="inline-flex items-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors group" data-id="element-329" data-mp="q5faw4te0y84">
              GET IN TOUCH
              <ArrowRight size={20} aria-hidden="true" className="group-hover:translate-x-1 transition-transform" data-id="element-330" data-mp="30m7ikdu6r1ca" />
            </Link>
          </div>
        </section>

        <NewsletterSignup data-id="element-331" data-mp="39kkkc60eyqwl" />

        <footer className="border-t border-white/10 py-12 text-center" data-id="element-332" data-mp="39ogp6smc2ake">
          <p className="font-bebas text-2xl text-white/50" data-id="element-333" data-mp="3emi91frl2dwx">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}